import { defineComponent as _defineComponent } from 'vue'
import {
  nextTick, onBeforeUnmount, ref, watch
} from 'vue';
import { useRoute } from 'vue2-helpers/vue-router';
import useInfoPopupState from '@/state/infoPopupState';
import useTokenState from '@/state/tokenState';
import usePageState from '@/state/pageState';
import {
  lockDocumentScroll,
  unlockDocumentScroll,
} from '@/utils/documentScrollLock';


export default /*#__PURE__*/_defineComponent({
  __name: 'InfoPopup',
  setup(__props) {

const {
  infoPopupComponent, markRead, hideInfoPopup, resetInfoPopupState
} = useInfoPopupState();
const { isLoggedIn } = useTokenState();
const { globalErrorMessage } = usePageState();
const route = useRoute();
const dialogElement = ref<HTMLElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;
let isDocumentScrollLocked = false;

const focusableSelector = [
  'button:not([disabled])',
  'a[href]',
  'input:not([disabled])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  '[tabindex]:not([tabindex="-1"])',
].join(',');

const getFocusableElements = () => (
  Array.from(dialogElement.value?.querySelectorAll<HTMLElement>(focusableSelector) ?? [])
    .filter((element) => element.getAttribute('aria-hidden') !== 'true')
);

const focusDialog = async () => {
  await nextTick();
  dialogElement.value?.focus();
};

const restoreFocus = () => {
  if (previouslyFocusedElement?.isConnected) previouslyFocusedElement.focus();
  previouslyFocusedElement = null;
};

const lockScroll = () => {
  if (isDocumentScrollLocked) return;
  lockDocumentScroll();
  isDocumentScrollLocked = true;
};

const unlockScroll = () => {
  if (!isDocumentScrollLocked) return;
  unlockDocumentScroll();
  isDocumentScrollLocked = false;
};

const trapFocus = (event: KeyboardEvent) => {
  const focusableElements = getFocusableElements();
  const firstFocusableElement = focusableElements[0];
  const lastFocusableElement = focusableElements[focusableElements.length - 1];

  if (!firstFocusableElement || !lastFocusableElement) {
    event.preventDefault();
    dialogElement.value?.focus();
    return;
  }

  if (document.activeElement === dialogElement.value) {
    event.preventDefault();
    (event.shiftKey ? lastFocusableElement : firstFocusableElement).focus();
    return;
  }

  if (!dialogElement.value?.contains(document.activeElement)) {
    event.preventDefault();
    (event.shiftKey ? lastFocusableElement : firstFocusableElement).focus();
    return;
  }

  if (event.shiftKey && document.activeElement === firstFocusableElement) {
    event.preventDefault();
    lastFocusableElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastFocusableElement) {
    event.preventDefault();
    firstFocusableElement.focus();
  }
};

const closeInfoPopup = async () => {
  const markedRead = await markRead();
  if (markedRead !== false) return;

  globalErrorMessage.value = {
    code: 'info-read',
    i18Key: 'common.unknownError',
    component: 'InfoPopup',
    withoutReload: true,
  };
};

watch(() => route.fullPath, hideInfoPopup);
watch(isLoggedIn, (loggedIn) => {
  if (!loggedIn) resetInfoPopupState();
});
watch(infoPopupComponent, async (component, previousComponent) => {
  if (component) {
    previouslyFocusedElement = document.activeElement as HTMLElement | null;
    lockScroll();
    await focusDialog();
  } else if (previousComponent) {
    unlockScroll();
    restoreFocus();
  }
});
onBeforeUnmount(() => {
  unlockScroll();
  restoreFocus();
  hideInfoPopup();
});

return { __sfc: true,infoPopupComponent, markRead, hideInfoPopup, resetInfoPopupState, isLoggedIn, globalErrorMessage, route, dialogElement, previouslyFocusedElement, isDocumentScrollLocked, focusableSelector, getFocusableElements, focusDialog, restoreFocus, lockScroll, unlockScroll, trapFocus, closeInfoPopup }
}

})